import * as Highcharts from 'highcharts';
import HighchartsReact from 'highcharts-react-official';
import highchartsBoost from 'highcharts/modules/boost';
import { useAppContext } from '../../contexts/AppContext';
import { useMemo, useRef } from 'react';
import { Backdrop, Box, CircularProgress, Typography } from '@mui/material';
import { buildTimeSeriesOptions, type TimeSeriesAxisOptions, type TimeSeriesChartData } from '../../utils/chartOptions/timeSeries';

highchartsBoost(Highcharts);

export type ChartData = TimeSeriesChartData;

type Props = TimeSeriesAxisOptions & {
  data: TimeSeriesChartData | null;
};

const Chart = ({ data, ymin, ymax, datetimeAxis, marginLeft, marginRight, tickInterval }: Props) => {
  const { isDarkMode } = useAppContext();
  const chartComponent = useRef(null);
  const isLoading = false;

  // isDarkMode is a dependency so the chart is redrawn after a theme switch.
  const chartOptions = useMemo(
    () => buildTimeSeriesOptions(data, { ymin, ymax, datetimeAxis, marginLeft, marginRight, tickInterval }),
    [data, isDarkMode, ymin, ymax, datetimeAxis, marginLeft, marginRight, tickInterval],
  );

  return (
    <>
      <Box sx={{ justifyContent: 'center', pl: 1, pr: 1 }}>
        {isLoading ? (
          <Backdrop open={true} sx={{ width: '100%', height: '450px', position: 'relative' }}>
            <CircularProgress />
          </Backdrop>
        ) : chartOptions ? (
          <HighchartsReact
            ref={chartComponent}
            containerProps={{ className: isDarkMode ? 'dark-chart' : '', style: { width: '100%' } }}
            highcharts={Highcharts}
            options={chartOptions}
          />
        ) : (
          <Typography color="text.secondary">Loading chart...</Typography>
        )}
      </Box>
    </>
  );
};

export default Chart;
